import CircularGauge from '@/components/base/CircularGauge';
import { useTheme } from '@/hooks/useTheme';

export interface InverterGroup {
  key: string;
  displayName: string;
  manufacturer: string;
  model: string | null;
  loggerHost: string;
  currentKw: number | null;
  groupNominalKw: number | null;
  percentOfGroupNominal: number | null;
  effectiveCapKw: number | null;
  percentOfEffectiveCap: number | null;
  expectedInverterCount: number;
  availableInverterCount: number;
  statusRaw: number | null;
  statusTag: string | null;
  qualityFlag: string;
  lastReadAtUtc: string | null;
  notes?: string | null;
}

interface Props {
  group: InverterGroup;
  assetName?: string;
}

const STATUS_COLORS: Record<string, { bg: string; fg: string; ring: string }> = {
  Ok:        { bg: 'rgba(13,122,60,0.10)', fg: '#0D7A3C', ring: '#0D7A3C' },
  Warning:   { bg: 'rgba(245,158,11,0.10)', fg: '#A66B00', ring: '#F59E0B' },
  Fault:     { bg: 'rgba(209,34,41,0.10)', fg: '#D12229', ring: '#D12229' },
  Off:       { bg: 'rgba(107,107,112,0.10)', fg: '#6B6B70', ring: '#9B9BA4' },
};

function fmtKw(v: number | null) {
  if (v == null) return '—';
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 100)  return v.toFixed(1);
  return v.toFixed(2);
}

export default function InverterGroupCard({ group, assetName }: Props) {
  const { isDark } = useTheme();
  const statusKey = group.statusTag && STATUS_COLORS[group.statusTag] ? group.statusTag : 'Off';
  const status = STATUS_COLORS[statusKey];

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const mutedText = isDark ? '#6B6B70' : '#9B9BA4';
  const trackColor = isDark ? 'rgba(255,255,255,0.08)' : '#F0F0F2';
  const footerBorder = isDark ? 'rgba(255,255,255,0.07)' : '#F0F0F2';
  const capBg = isDark ? 'rgba(166,107,0,0.15)' : '#FFF4E6';
  const capText = isDark ? '#F59E0B' : '#A66B00';

  const sectorIdx = (() => {
    const m = group.key.match(/_(\d+)$/);
    return m ? m[1] : '';
  })();
  const title = assetName
    ? `${assetName.replace('СЕС ', '')} ${group.manufacturer}${sectorIdx ? ' ' + sectorIdx : ''}`.trim()
    : group.displayName;

  const isCurtailed = group.effectiveCapKw != null && group.groupNominalKw != null
    && group.effectiveCapKw < group.groupNominalKw;
  const curtailRatio = isCurtailed && group.groupNominalKw
    ? Math.round((group.effectiveCapKw! / group.groupNominalKw) * 100)
    : null;

  return (
    <div
      className="rounded-xl p-4 flex flex-col"
      style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate" style={{ color: textMain }}>
            {title}
          </div>
          <div className="text-[10px] mt-0.5 truncate" style={{ color: mutedText }}>
            {group.model || group.loggerHost}
          </div>
        </div>
        {isCurtailed && curtailRatio != null && (
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ml-2"
            style={{ background: capBg, color: capText }}
            title={`Curtailed to ${curtailRatio}% — effective cap ${group.effectiveCapKw?.toFixed(0)} kW`}
          >
            {curtailRatio}% cap
          </span>
        )}
      </div>

      {/* Main gauge */}
      <div className="flex items-center justify-center my-2">
        <CircularGauge
          value={group.currentKw}
          max={group.groupNominalKw ?? 1}
          size={120}
          strokeWidth={8}
          color={status.ring}
          trackColor={trackColor}
          muted={group.currentKw == null}
        >
          <span className="text-xl font-bold leading-none" style={{ color: textMain }}>
            {fmtKw(group.currentKw)}
          </span>
          <span className="text-[10px] mt-0.5" style={{ color: textMuted }}>kW</span>
          {group.percentOfGroupNominal != null && (
            <span className="text-[10px] mt-0.5 font-medium" style={{ color: textMuted }}>
              {group.percentOfGroupNominal}%
            </span>
          )}
        </CircularGauge>
      </div>

      {/* Footer: status + coverage */}
      <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: `1px solid ${footerBorder}` }}>
        <span
          className="text-[11px] font-medium px-2 py-0.5 rounded-full"
          style={{ background: status.bg, color: status.fg }}
        >
          {group.statusTag ?? '—'}
        </span>
        <span className="text-[11px]" style={{ color: textMuted }}>
          {group.availableInverterCount}/{group.expectedInverterCount} inv
        </span>
      </div>
    </div>
  );
}